import { useRouteError, isRouteErrorResponse } from "react-router";
import { AlertTriangle, RotateCw } from "lucide-react";

// Matches EnvironmentBanner's detection: details are shown wherever the
// environment banner shows (staging/development), never in production.
const ENV = import.meta.env.VITE_PORTAL_ENV;
const SHOW_TECHNICAL_DETAILS = ENV === "staging" || ENV === "development";

function describeError(error: unknown): { message: string; stack?: string } {
  if (isRouteErrorResponse(error)) {
    return {
      message: `${error.status} ${error.statusText}`,
      stack: typeof error.data === "string" ? error.data : JSON.stringify(error.data, null, 2),
    };
  }
  if (error instanceof Error) {
    return { message: error.message, stack: error.stack };
  }
  return { message: String(error) };
}

// Router-level error boundary: catches render crashes anywhere under the
// route it is attached to and replaces React Router's default red screen.
export function AppErrorScreen() {
  const error = useRouteError();
  const { message, stack } = describeError(error);

  // Always surface to the console so the error is still findable in
  // production, where the details panel is hidden.
  console.error("AppErrorScreen caught:", error);

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center p-6">
      <div className="w-full max-w-lg bg-paper-hi rounded-xl shadow-card p-8 text-center">
        <div className="mx-auto w-12 h-12 rounded-full bg-rust/10 flex items-center justify-center mb-4">
          <AlertTriangle size={22} strokeWidth={2} className="text-rust" />
        </div>
        <h1 className="font-display text-[22px] font-medium mb-2">
          Oops — something went wrong
        </h1>
        <p className="text-[13.5px] text-ink-3 mb-6">
          The page hit an unexpected error. Your data is safe — reloading
          usually fixes it.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-ink text-paper-hi text-[13px] font-medium hover:bg-ink-2"
        >
          <RotateCw size={14} strokeWidth={2} />
          Reload
        </button>

        {SHOW_TECHNICAL_DETAILS && (
          <details className="mt-8 text-left">
            <summary className="cursor-pointer text-[12px] text-ink-4 hover:text-ink-2 select-none">
              Technical details ({ENV} only)
            </summary>
            <pre className="mt-3 p-4 rounded-md bg-ink text-paper-hi/90 text-[11px] leading-relaxed overflow-auto max-h-80 whitespace-pre-wrap break-words">
              {message}
              {stack ? `\n\n${stack}` : ""}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}
